import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { PlatformService } from "@obg/ngx.global.evo";
import { isEmpty } from "lodash-es";
import { combineLatest, EMPTY, Observable, of } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";

import { AuthSelector } from "../../../auth/auth.selector";
import { DisallowedActionCodes } from "../../../customer-management/customer-management.model";
import { CustomerManagementSelector } from "../../../customer-management/customer-management.selector";
import { CoreState } from "../../../state";
import { DepositLimitAction } from "../deposit-limit.action";
import { DepositLimitConfigService } from "../deposit-limit.config";
import { DepositLimitSelector } from "../deposit-limit.selector";

const DEPOSIT_LIMIT_DIALOG_SHOWN = "DEPOSIT_LIMIT_DIALOG_SHOWN";

/**
 * @deprecated [fabric-deprecated] - Use DepositLimitService from app-responsible-gaming and CustomerManagementService from app-account instead
 */
@Injectable({
	providedIn: "root",
})
export class DepositLimitRequiredService {
	constructor(
		private store: Store<CoreState>,
		private depositLimitAction: DepositLimitAction,
		private depositLimitSelector: DepositLimitSelector,
		private config: DepositLimitConfigService,
		private platform: PlatformService
	) {}

	showDepositLimitsDialog$(): Observable<void> {
		return this.config.getIsDepositLimitRequiredWithDialog() && this.platform.isBrowser
			? this.store.pipe(
					AuthSelector.isAuthenticated$,
					switchMap((isAuth) => {
						if (!isAuth) {
							sessionStorage.removeItem(DEPOSIT_LIMIT_DIALOG_SHOWN);
							return EMPTY;
						}
						if (sessionStorage.getItem(DEPOSIT_LIMIT_DIALOG_SHOWN)) {
							return EMPTY;
						}

						return this.isDepositLimitsMissing$().pipe(
							filter((depositLimitsMissing) => depositLimitsMissing),
							tap(() => sessionStorage.setItem(DEPOSIT_LIMIT_DIALOG_SHOWN, "true")),
							map(() => undefined)
						);
					})
				)
			: EMPTY;
	}

	isDepositLimitsMissing$(): Observable<boolean> {
		return this.store.select(AuthSelector.isAuthenticated).pipe(
			filter((isAuth) => isAuth),
			switchMap(() =>
				combineLatest([
					this.store.select(this.depositLimitSelector.isBusy()),
					this.store.select(this.depositLimitSelector.getAll()),
				]).pipe(
					filter(([isBusy]) => !isBusy),
					map(([, limits]) => limits),
					tap((limits) => {
						if (limits === undefined) {
							this.store.dispatch(this.depositLimitAction.getAll());
						}
					}),
					filter((limits) => !!limits),
					map(isEmpty),
					take(1)
				)
			)
		);
	}

	shouldOpenDepositLimit$(): Observable<boolean> {
		return this.store.pipe(
			AuthSelector.isAuthenticated$,
			switchMap((isAuth) =>
				!isAuth
					? of(false)
					: combineLatest([
							this.store.select(CustomerManagementSelector.isBusy),
							this.store.select(CustomerManagementSelector.getCustomer),
						]).pipe(
							filter(([isBusy, customer]) => !isBusy && !!customer),
							switchMap(() => this.isDepositLimitUnset$())
						)
			)
		);
	}

	/**
	 * @deprecated [fabric-deprecated] - Use AccountAppValue.GetCustomerAllowedActions instead
	 *
	 * Used for SGA
	 */
	isDepositLimitUnset$(): Observable<boolean> {
		return this.store
			.select(CustomerManagementSelector.getAllowedAction)
			.pipe(
				map(
					(actions) =>
						!actions?.betCasino?.isAllowed &&
						!!actions?.betCasino?.disallowedReasons.includes(
							DisallowedActionCodes.M_CURRENTCUSTOMER_DEPOSITLIMITUNSET
						)
				)
			);
	}
}
